import React, { useState } from "react";
import { User, Lock, FileText } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { BRAND, waLink } from "@/data/brand";
import { DEMO_CLIENT, DEMO_ORDERS, eur } from "@/data/clientAreaDemo";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { WhatsAppIcon } from "@/components/WhatsAppButton";

const fmtDate = (iso, lang) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(lang, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

// ---- Login -----------------------------------------------------------------
export function LoginDialog({ open, onOpenChange, onLogin }) {
  const { t } = useLang();
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [error, setError] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (onLogin(user, pass)) {
      setError(false);
      setUser("");
      setPass("");
      onOpenChange(false);
    } else {
      setError(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="access-modal"
        className="bg-[#112318] border border-white/12 rounded-none text-white max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-black uppercase tracking-tight flex items-center gap-2">
            <User size={20} /> {t.area.loginTitle}
          </DialogTitle>
          <DialogDescription className="text-white/60 font-light pt-1">
            {t.area.loginSub}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 pt-2">
          <label className="block">
            <span className="font-mono text-[11px] uppercase tracking-widest text-white/60">
              {t.area.user}
            </span>
            <div className="mt-1.5 flex items-center gap-2.5 border border-white/15 bg-black/30 px-3.5 focus-within:border-[color:var(--dp-orange)] transition-colors">
              <User size={15} className="text-white/40 shrink-0" />
              <input
                data-testid="area-login-user"
                value={user}
                onChange={(e) => setUser(e.target.value)}
                autoComplete="username"
                className="w-full bg-transparent py-3 text-sm outline-none placeholder:text-white/30"
              />
            </div>
          </label>
          <label className="block">
            <span className="font-mono text-[11px] uppercase tracking-widest text-white/60">
              {t.area.pass}
            </span>
            <div className="mt-1.5 flex items-center gap-2.5 border border-white/15 bg-black/30 px-3.5 focus-within:border-[color:var(--dp-orange)] transition-colors">
              <Lock size={15} className="text-white/40 shrink-0" />
              <input
                data-testid="area-login-pass"
                type="password"
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                autoComplete="current-password"
                className="w-full bg-transparent py-3 text-sm outline-none placeholder:text-white/30"
              />
            </div>
          </label>

          {error && (
            <p data-testid="area-login-error" className="text-sm text-red-400">
              {t.area.wrong}
            </p>
          )}

          <button
            data-testid="area-login-submit"
            type="submit"
            className="w-full py-3.5 bg-[color:var(--dp-orange)] hover:bg-[color:var(--dp-orange-hover)] text-black font-semibold text-sm tracking-tight transition-colors"
          >
            {t.area.enter}
          </button>
        </form>

        <a
          href={waLink(t.contact.wa.prefill)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="access-whatsapp"
          className="mt-4 flex items-center justify-center gap-2 border border-white/12 hover:border-[#25D366] py-3 text-sm text-white/80 transition-colors"
        >
          <WhatsAppIcon size={16} className="text-[#25D366]" />
          {t.area.noAccess} <span className="font-semibold text-white">{t.area.contactUs}</span>
        </a>
      </DialogContent>
    </Dialog>
  );
}

// ---- Històric de comandes ----------------------------------------------------
export function HistoryDialog({ open, onOpenChange }) {
  const { t, lang } = useLang();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="history-modal"
        className="bg-[#112318] border border-white/12 rounded-none text-white max-w-2xl max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-black uppercase tracking-tight flex items-center gap-3">
            {t.area.historyTitle}
            <span
              data-testid="area-demo-badge"
              className="px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest bg-[color:var(--dp-orange)] text-black"
            >
              {t.area.badge}
            </span>
          </DialogTitle>
          <DialogDescription className="text-white/60 font-light pt-1">
            {DEMO_CLIENT.name} — {DEMO_CLIENT.company}
          </DialogDescription>
        </DialogHeader>

        <div data-testid="area-orders-table" className="overflow-x-auto border border-white/10 mt-2">
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="px-4 py-3 text-left font-mono text-[11px] uppercase tracking-widest text-white/50 font-normal">
                  {t.area.th.id}
                </th>
                <th className="px-4 py-3 text-left font-mono text-[11px] uppercase tracking-widest text-white/50 font-normal">
                  {t.area.th.date}
                </th>
                <th className="px-4 py-3 text-left font-mono text-[11px] uppercase tracking-widest text-white/50 font-normal">
                  {t.area.th.items}
                </th>
                <th className="px-4 py-3 text-right font-mono text-[11px] uppercase tracking-widest text-white/50 font-normal">
                  {t.area.th.total}
                </th>
                <th className="px-4 py-3 text-right font-mono text-[11px] uppercase tracking-widest text-white/50 font-normal"></th>
              </tr>
            </thead>
            <tbody>
              {DEMO_ORDERS.map((o) => (
                <tr
                  key={o.id}
                  data-testid={`area-order-${o.id}`}
                  className="border-t border-white/8 hover:bg-white/[0.03] transition-colors"
                >
                  <td className="px-4 py-3.5 text-sm font-semibold whitespace-nowrap">{o.id}</td>
                  <td className="px-4 py-3.5 text-sm text-white/70 whitespace-nowrap">
                    {fmtDate(o.date, lang)}
                  </td>
                  <td className="px-4 py-3.5 text-sm text-white/70 whitespace-nowrap">{o.items}</td>
                  <td className="px-4 py-3.5 text-sm font-semibold text-right whitespace-nowrap">
                    {eur(o.total)}
                  </td>
                  <td className="px-4 py-3.5 text-right whitespace-nowrap">
                    <a
                      data-testid={`area-invoice-${o.id}`}
                      href={`mailto:${BRAND.email}?subject=${encodeURIComponent(`${t.area.invSubject}: ${o.id}`)}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-white/15 hover:border-[color:var(--dp-orange)] hover:text-[color:var(--dp-orange)] text-xs font-mono uppercase tracking-widest transition-colors"
                    >
                      <FileText size={13} /> {t.area.invoice}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p data-testid="area-demo-note" className="mt-4 text-xs text-amber-300/80">
          {t.area.note}
        </p>
      </DialogContent>
    </Dialog>
  );
}
